import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, BookUser, Download, Search, GraduationCap, Gift } from "lucide-react";
import { API, authHeaders } from "./AdminPage";
import { useAdminT } from "./adminI18n";

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

export const ContactsTab = () => {
  const { t } = useAdminT();
  const [contacts, setContacts] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admin/contacts`, { headers: authHeaders() })
      .then((res) => setContacts(res.data))
      .catch(() => setContacts([]));
  }, []);

  const filtered = useMemo(() => {
    if (!contacts) return [];
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => (c.name || "").toLowerCase().includes(q) || c.email.includes(q));
  }, [contacts, query]);

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Type", "Courses", "Sources", "First seen", "Last seen"]];
    filtered.forEach((c) =>
      rows.push([
        c.name,
        c.email,
        c.phone || "",
        c.kinds.includes("customer") ? "customer" : "lead",
        (c.courses || []).map((x) => `${x.title}${x.expires_at ? ` (until ${x.expires_at.slice(0, 10)})` : " (lifetime)"}`).join(" | "),
        (c.sources || []).join(" | "),
        (c.first_seen || "").slice(0, 16).replace("T", " "),
        (c.last_seen || "").slice(0, 16).replace("T", " "),
      ])
    );
    const csv = rows.map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "themaya-contacts.csv";
    a.click();
  };

  if (contacts === null) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-contacts-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-serif text-3xl text-luxury-bone">{t("جهات الاتصال")} ({contacts.length})</h2>
        {contacts.length > 0 && (
          <button data-testid="contacts-export-button" onClick={exportCsv}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors">
            <Download className="w-4 h-4" strokeWidth={2} />
            {t("تصدير CSV")}
          </button>
        )}
      </div>
      <p className="text-luxury-gray text-sm mb-6">
        {t("كل عملائك في مكان واحد — تُحفظ بياناتهم تلقائياً عند تحميل الهدية المجانية (مثل اليوميات) أو عند منحهم وصولاً لأي دورة.")}
      </p>

      <div className="relative mb-8">
        <Search className="w-4 h-4 text-luxury-gray absolute top-1/2 -translate-y-1/2 start-4" strokeWidth={1.5} />
        <input
          data-testid="contacts-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("ابحثي بالاسم أو الإيميل")}
          className={`${inputCls} ps-11 rounded-full`}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-luxury-gray">
          <BookUser className="w-10 h-10 mx-auto mb-4 text-luxury-gold" strokeWidth={1} />
          {t("لا توجد جهات اتصال بعد")}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c, i) => (
            <div key={c.email} data-testid={`contact-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-luxury-bone font-semibold">{c.name || "—"}</p>
                  <p className="text-luxury-gold text-sm" dir="ltr">{c.email}</p>
                  {c.phone && <p className="text-luxury-gray text-sm mt-1" dir="ltr">{c.phone}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    {c.kinds.includes("customer") && (
                      <span className="flex items-center gap-1.5 text-xs border border-luxury-gold/50 text-luxury-gold rounded-full px-3 py-1">
                        <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("عميل دورة")}
                      </span>
                    )}
                    {c.kinds.includes("lead") && (
                      <span className="flex items-center gap-1.5 text-xs border border-luxury-border text-luxury-gray rounded-full px-3 py-1">
                        <Gift className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("سجّل بالهدية")}
                      </span>
                    )}
                  </div>
                  <p className="text-luxury-gray text-xs" dir="ltr">{(c.last_seen || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
              {c.courses.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {c.courses.map((x, j) => (
                    <span key={j} data-testid={`contact-course-${i}-${j}`}
                      className={`text-xs rounded-full px-3 py-1.5 border ${x.active ? "border-luxury-gold/40 text-luxury-bone" : "border-red-400/40 text-red-400"}`}>
                      {x.title} — {x.expires_at ? `${t("حتى")} ${x.expires_at.slice(0, 10)}` : t("مدى الحياة")}
                      {!x.active && ` (${t("منتهية")})`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
